/**
 * Cuándo y dónde se juega un partido.
 *
 * SE ABRE SOBRE EL CUADRO, igual que el editor de marcador, y por la misma
 * razón: quien programa está mirando el cuadro y decidiendo con él a la
 * vista. Mandarlo a otra pantalla lo obliga a acordarse de qué partido era.
 *
 * EL «REPETIR EN TODOS» COPIA EL LUGAR, LA CANCHA Y LA NOTA, y no la fecha ni
 * la hora. Es la diferencia entre lo que de verdad se repite —el torneo entero
 * se juega en el mismo lugar— y lo que por definición no puede repetirse: dos
 * partidos a la misma hora en la misma cancha no se pueden jugar. Copiar la
 * hora sería ofrecer un botón que arma un torneo imposible.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Match, MatchSchedule } from '@tennisup/contracts';

interface Props {
  match: Match;
  onCancel: () => void;
  onSave: (schedule: MatchSchedule, applyPlaceToAll: boolean) => Promise<void> | void;
}

export function ScheduleEditor({ match, onCancel, onSave }: Props): JSX.Element {
  const { t } = useTranslation();
  const [date, setDate] = useState(match.schedule.date ?? '');
  const [time, setTime] = useState(match.schedule.time ?? '');
  const [place, setPlace] = useState(match.schedule.place ?? '');
  const [court, setCourt] = useState(match.schedule.court ?? '');
  const [notes, setNotes] = useState(match.schedule.notes ?? '');
  const [repetir, setRepetir] = useState(false);
  const [sending, setSending] = useState(false);

  const quienes = [match.home?.displayName, match.away?.displayName]
    .filter((x): x is string => x !== undefined)
    .join(' · ');

  async function guardar(): Promise<void> {
    setSending(true);
    try {
      await onSave(
        {
          // Vacío es «quitalo», no «dejalo como estaba»: quien borra el campo
          // quiere borrar la fecha.
          date: date === '' ? null : date,
          time: time === '' ? null : time,
          place: place.trim() === '' ? null : place.trim(),
          court: court.trim() === '' ? null : court.trim(),
          notes: notes.trim() === '' ? null : notes.trim(),
        },
        repetir,
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="schedule-editor">
      <h3>{t('schedule.title')}</h3>
      {quienes !== '' && <p className="help">{quienes}</p>}

      <div className="row">
        <div className="field">
          <label htmlFor="sch-date">{t('schedule.date')}</label>
          <input id="sch-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="sch-time">{t('schedule.time')}</label>
          <input id="sch-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="sch-place">{t('schedule.place')}</label>
          <input
            id="sch-place"
            type="text"
            maxLength={120}
            value={place}
            placeholder={t('schedule.placeExample')}
            onChange={(e) => setPlace(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="sch-court">{t('schedule.court')}</label>
          <input
            id="sch-court"
            type="text"
            maxLength={60}
            value={court}
            placeholder={t('schedule.courtExample')}
            onChange={(e) => setCourt(e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="sch-notes">{t('schedule.notes')}</label>
        <input
          id="sch-notes"
          type="text"
          maxLength={300}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <label className="check">
        <input
          type="checkbox"
          checked={repetir}
          onChange={(e) => setRepetir(e.target.checked)}
        />
        <span>{t('schedule.repeat')}</span>
      </label>
      {/* Se dice EXACTAMENTE qué copia y qué no, antes de apretarlo. Un
          «repetir en todos» sin esa línea se entiende como «copiá todo», y
          el día y la hora son justo lo que no se puede copiar. */}
      <p className="help">{t('schedule.repeatHelp')}</p>

      <div className="button-row">
        <button type="button" disabled={sending} onClick={() => void guardar()}>
          {t('common.save')}
        </button>
        <button type="button" className="link" disabled={sending} onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
